import services from "@/composable/services";
import { useI18n } from "vue-i18n";

/**
 * Options pour {@link useConfirmAction#confirmAction} . Toutes les chaines
 * affichees sont fournies sous forme de cles i18n ( pas de texte en dur )
 * pour garantir la traduction FR / EN et la coherence du libelle entre
 * tous les ecrans qui declenchent la meme action destructive .
 */
export interface ConfirmActionOptions {
  /** Cle i18n du titre de la popup ( ex {@code "alert.confirm-delete-authorization-title"} ) . */
  titleKey: string;
  /** Cle i18n du message ( typiquement une mise en garde detaillee sur l'impact metier ) . */
  messageKey: string;
  /** Cle i18n du libelle du bouton de confirmation ( ex {@code "alert.confirm-delete-authorization-confirm"} ) . */
  confirmTextKey: string;
  /** Cle i18n du libelle du bouton annuler . Defaut : {@code "alert.cancel"} . */
  cancelTextKey?: string;
  /** Type visuel passe a Buefy ( "warning" , "danger" , ... ) . Defaut : {@code "warning"} . */
  type?: string;
  /** Callback executee uniquement si l'utilisateur clique sur Confirmer . */
  action: () => Promise<void> | void;
}

/**
 * Type d'arguments positionnels attendus par {@link services.alertService.confirm} .
 * Permet a {@link buildConfirmArgs} de rester pure et testable sans avoir a
 * mocker {@code services} ni {@code useI18n} .
 */
export interface ConfirmArgs {
  title: string;
  message: string;
  confirmText: string;
  type: string;
  cancel: string;
  onConfirm: () => void;
}

/**
 * Pure : traduit les {@link ConfirmActionOptions} en arguments concrets pour
 * {@code alertService.confirm} . Extrait pour etre testable unitairement
 * sans avoir a monter Vue + i18n ( on peut injecter un {@code t} mocke
 * directement ) .
 */
export function buildConfirmArgs(
  opts: ConfirmActionOptions,
  t: (key: string) => string,
): ConfirmArgs {
  return {
    title: t(opts.titleKey),
    message: t(opts.messageKey),
    confirmText: t(opts.confirmTextKey),
    type: opts.type ?? "warning",
    cancel: t(opts.cancelTextKey ?? "alert.cancel"),
    onConfirm: () => { void opts.action(); },
  };
}

/**
 * Composable qui factorise le pattern "popup de confirmation avant une
 * action destructive" ( suppression d'autorisation , revocation
 * d'utilisateur , purge de cache , ... ) . Sans ce composable , chaque
 * site d'appel duplique le wiring vers {@link services.alertService.confirm}
 * + les 4-5 cles i18n necessaires , avec un risque de divergence ( labels
 * differents pour la meme action , bouton de confirmation non i18n , etc ) .
 *
 * <p>Usage type :
 * <pre>
 *   const { confirmAction } = useConfirmAction();
 *   function deleteSomething(id: string) {
 *     confirmAction({
 *       titleKey: "alert.confirm-delete-foo-title",
 *       messageKey: "alert.confirm-delete-foo-message",
 *       confirmTextKey: "alert.confirm-delete-foo-confirm",
 *       type: "danger",
 *       action: () => performDeleteFoo(id),
 *     });
 *   }
 * </pre>
 *
 * <p><b>Doit etre appele dans un contexte de setup Vue</b> car il utilise
 * {@code useI18n} en interne .
 */
export function useConfirmAction(): { confirmAction: (opts: ConfirmActionOptions) => void } {
  const { t } = useI18n();

  function confirmAction(opts: ConfirmActionOptions): void {
    const args = buildConfirmArgs(opts, t);
    services.alertService.confirm(
      args.title,
      args.message,
      args.confirmText,
      args.type,
      args.cancel,
      args.onConfirm,
    );
  }

  return { confirmAction };
}
